/** AuditoriaPage — Vista global e inmutable de la auditoría del módulo Tickets.
 *
 * Filtros: rango de fechas (presets + custom), usuario, acción (búsqueda libre),
 * código de ticket. Paginación simple por offset/limit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ClipboardList, Loader2, ChevronLeft, ChevronRight, AlertCircle, Move } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const PRESETS = [
  { key: 'mes', label: 'Mes actual' },
  { key: '7d',  label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: '90d', label: '90 días' },
  { key: 'all', label: 'Todo' },
];
const PAGE = 50;

const fmtDate = (s) => s ? new Date(s).toLocaleString('es-PE') : '—';
const startOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function AuditoriaPage() {
  const { api, empresa } = useAuth();
  const [preset, setPreset] = useState('mes');
  const [desde, setDesde] = useState(startOfMonth());
  const [hasta, setHasta] = useState(todayISO());
  const [usuarioId, setUsuarioId] = useState('all');
  const [accion, setAccion] = useState('');
  const [ticketCodigo, setTicketCodigo] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  const aplicarPreset = (key) => {
    setPreset(key);
    setOffset(0);
    if (key === 'mes') { setDesde(startOfMonth()); setHasta(todayISO()); }
    else if (key === 'all') { setDesde(''); setHasta(''); }
    else if (key === '7d')  { setDesde(daysAgoISO(7));  setHasta(todayISO()); }
    else if (key === '30d') { setDesde(daysAgoISO(30)); setHasta(todayISO()); }
    else if (key === '90d') { setDesde(daysAgoISO(90)); setHasta(todayISO()); }
  };

  const fetchData = useCallback(async () => {
    if (!empresa?.modulo_tickets_activo) { setLoading(false); return; }
    // Cancela cualquier petición previa en curso (cambios rápidos de filtro / desmontaje)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (usuarioId !== 'all') params.usuario_id = usuarioId;
      if (accion.trim()) params.accion = accion.trim();
      if (ticketCodigo.trim()) params.ticket_codigo = ticketCodigo.trim();
      const { data: res } = await api.get('/tickets/audit', { params, signal: controller.signal });
      setData(res || { items: [], total: 0 });
    } catch (e) {
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
        console.error('Error cargando auditoría:', e);
      }
    } finally {
      // Solo apaga el loading si esta petición sigue siendo la vigente
      if (abortRef.current === controller) setLoading(false);
    }
  }, [api, empresa, offset, desde, hasta, usuarioId, accion, ticketCodigo]);

  useEffect(() => {
    if (!empresa?.modulo_tickets_activo) { setLoading(false); return; }
    const controller = new AbortController();
    api.get('/tickets/responsables', { signal: controller.signal })
      .then((r) => setUsuarios(r.data || []))
      .catch(() => {});
    return () => controller.abort();
  }, [api, empresa]);

  useEffect(() => {
    fetchData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE)), [data]);
  const currentPage = Math.floor(offset / PAGE) + 1;

  if (!empresa?.modulo_tickets_activo) {
    return (
      <div className="container mx-auto py-8 max-w-3xl">
        <Card><CardContent className="py-10 text-center text-slate-500">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <p className="font-medium text-slate-800">El módulo Centro de Ayuda no está activado.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-4 max-w-[1500px]">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="w-6 h-6 text-slate-700" />
        <h1 className="text-2xl font-bold text-slate-800">Auditoría — Centro de Ayuda</h1>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={preset === p.key ? 'default' : 'outline'}
                onClick={() => aplicarPreset(p.key)}
                data-testid={`audit-preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
            <div className="flex items-center gap-1 ml-2">
              <Input type="date" value={desde} onChange={(e) => { setPreset('custom'); setOffset(0); setDesde(e.target.value); }} className="h-9 w-[150px]" data-testid="audit-desde" />
              <span className="text-xs text-slate-400">→</span>
              <Input type="date" value={hasta} onChange={(e) => { setPreset('custom'); setOffset(0); setHasta(e.target.value); }} className="h-9 w-[150px]" data-testid="audit-hasta" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Select value={usuarioId} onValueChange={(v) => { setUsuarioId(v); setOffset(0); }}>
              <SelectTrigger className="h-9" data-testid="audit-usuario"><SelectValue placeholder="Usuario" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Acción (ej: ticket_actualizado)"
              value={accion}
              onChange={(e) => { setOffset(0); setAccion(e.target.value); }}
              className="h-9"
              data-testid="audit-accion"
            />
            <Input
              placeholder="Código ticket (ej: TCK-2026-00001)"
              value={ticketCodigo}
              onChange={(e) => { setOffset(0); setTicketCodigo(e.target.value); }}
              className="h-9 font-mono"
              data-testid="audit-codigo"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Entradas {data.total != null && <span className="text-xs text-slate-500 ml-2">({data.total} totales)</span>}</span>
            <div className="flex items-center gap-1 text-xs">
              <Button size="sm" variant="outline" className="h-7 px-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="audit-prev">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-slate-500 px-2">{currentPage} / {totalPages}</span>
              <Button size="sm" variant="outline" className="h-7 px-2" disabled={currentPage >= totalPages} onClick={() => setOffset(offset + PAGE)} data-testid="audit-next">
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AuditTable items={data.items} loading={loading} />
        </CardContent>
      </Card>
    </div>
  );
}

export function AuditTable({ items, loading, hideTicketColumn = false, scrollable = false }) {
  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>;
  }
  if (!items?.length) {
    return <p className="text-sm text-slate-400 italic text-center py-10">Sin entradas en este rango.</p>;
  }
  return (
    <div className={`border rounded-md ${scrollable ? 'overflow-x-auto' : 'overflow-hidden'}`}>
      <table className={`w-full text-xs ${scrollable ? 'min-w-[520px]' : ''}`} data-testid="audit-table">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Fecha</th>
            <th className="text-left px-3 py-2 font-medium">Usuario</th>
            <th className="text-left px-3 py-2 font-medium">Acción</th>
            {!hideTicketColumn && <th className="text-left px-3 py-2 font-medium">Ticket</th>}
            <th className="text-left px-3 py-2 font-medium">Cambios</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t hover:bg-slate-50 align-top">
              <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtDate(it.fecha)}</td>
              <td className="px-3 py-2 text-slate-700">{it.usuario_nombre || '—'}</td>
              <td className="px-3 py-2"><AccionBadge accion={it.accion} /></td>
              {!hideTicketColumn && (
                <td className="px-3 py-2 font-mono text-slate-700">
                  {it.ticket_codigo ? (
                    <Link to={`/admin/centro-ayuda/tickets/${it.ticket_id}`} className="text-blue-600 hover:underline">
                      {it.ticket_codigo}
                    </Link>
                  ) : '—'}
                </td>
              )}
              <td className="px-3 py-2 text-[11px]">
                <DiffCell antes={it.valor_antes} despues={it.valor_despues} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccionBadge({ accion }) {
  if (accion === 'kanban_drag') {
    return (
      <Badge
        variant="outline"
        className="text-[10px] inline-flex items-center gap-1 border-blue-300 bg-blue-50 text-blue-700"
        data-testid="badge-accion-kanban_drag"
      >
        <Move className="w-3 h-3" />
        kanban_drag
      </Badge>
    );
  }
  return <Badge variant="outline" className="text-[10px]" data-testid={`badge-accion-${accion}`}>{accion}</Badge>;
}

function DiffCell({ antes, despues }) {
  if (!antes && !despues) return <span className="text-slate-400">—</span>;
  const keys = new Set([...Object.keys(antes || {}), ...Object.keys(despues || {})]);
  if (keys.size === 0) return <span className="text-slate-400">—</span>;
  return (
    <ul className="space-y-0.5">
      {[...keys].map((k) => (
        <li key={k}>
          <strong className="text-slate-600">{k}:</strong>{' '}
          {k === 'resend_id' ? (
            <ResendIdChip value={despues?.[k]} />
          ) : (
            <>
              <span className="line-through text-rose-700">{stringify(antes?.[k])}</span>{' '}
              <span className="text-slate-400">→</span>{' '}
              <span className="text-emerald-700">{stringify(despues?.[k])}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function ResendIdChip({ value }) {
  if (!value) return <span className="text-slate-400 italic">∅ (envío sin id)</span>;
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { navigator.clipboard?.writeText(value); } catch { /* noop */ }
  };
  return (
    <span className="inline-flex items-center gap-1 align-middle" data-testid="audit-resend-id">
      <code
        className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 cursor-copy hover:bg-slate-200"
        onClick={handleCopy}
        title="Click para copiar"
      >
        {value}
      </code>
      <a
        href={`https://resend.com/emails/${value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline text-[10px]"
        title="Abrir en panel Resend"
      >
        ↗
      </a>
    </span>
  );
}

function stringify(v) {
  if (v == null) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
